import { useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";

import { apiFormat, serverResponseFormat, shortMonthAndFullYear } from "@similarweb/pro-logic-components";

import { swSettings } from "app/common/services/swSettings";
import { useColorStack } from "app/custom-hooks/useColorStack";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { webSourceToDeviceForApi } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { CHART_COLORS } from "app/constants/ChartColors";

import type { ITransformedSerpItem } from "./serpDataTransform";
import { transformSerpFeaturesDistribution } from "./serpDataTransform";
import { SERPFeaturesComposition } from "./SERPFeaturesComposition";
import { translate } from "app/filters/i18nFilter";

const SERP_FEATURES_ENDPOINT = "api/KeywordAnalysis/Overview/SerpFeaturesPresence";

interface SerpFeaturesPresenceItem {
    Date: string;
    SerpFeaturesPresence: Record<string, number>;
}

interface SerpFeaturesPresenceWrappedResponse {
    SerpFeaturesPresenceRecords: SerpFeaturesPresenceItem[];
}

export const SERPFeaturesCompositionKeywordsContainer = () => {
    const { params } = useCurrentStateAndParams();
    const { duration, webSource, country, keyword, pageFilterJson, sourceType } = params;

    const { from, to, isWindow } = DurationService.getDurationData(duration).forAPI;
    const calculatedModifiedFrom = dayjs.utc(from, serverResponseFormat).subtract(1, "year");

    const latest12Months = DurationService.getDurationData(BasicDurations.LAST_TWELVE_MONTHS).forAPI;
    const startDate = dayjs.utc(swSettings.current.resources.StartDate);
    const finalFrom = startDate.isAfter(calculatedModifiedFrom) ? startDate : calculatedModifiedFrom;

    const latest12MonthsFromDate = dayjs.utc(latest12Months.from, apiFormat);

    const startForWindow = startDate.isAfter(latest12MonthsFromDate) ? startDate : latest12MonthsFromDate;

    const apiParams = {
        country,
        from: isWindow ? startForWindow.format(apiFormat) : finalFrom.format(apiFormat),
        to: isWindow ? latest12Months.to : to,
        isWindow: false,
        key: keyword.startsWith("*") ? keyword.slice(1) : keyword,
        pageFilterJson,
        device: webSourceToDeviceForApi[webSource],
        sourceType,
    };

    const { acquire, release, reset } = useColorStack(CHART_COLORS.chartMainColors);
    const [serpData, setSerpData] = useState<ITransformedSerpItem[]>([]);

    const {
        data,
        isLoading: serpIsLoading,
        isError: serpIsError,
    } = useQuery<SerpFeaturesPresenceWrappedResponse>({
        queryKey: [SERP_FEATURES_ENDPOINT, apiParams],
        queryFn: () => DefaultFetchService.getInstance().get(SERP_FEATURES_ENDPOINT, apiParams),
    });

    useEffect(() => {
        if (!serpIsLoading && !serpIsError && data?.SerpFeaturesPresenceRecords?.length) {
            reset();
            const serpData = data.SerpFeaturesPresenceRecords.map((record) => ({
                yearmonth: dayjs(record.Date).format(shortMonthAndFullYear),
                serpFeatures: record.SerpFeaturesPresence || {},
            }));
            const transformedData = transformSerpFeaturesDistribution(serpData, acquire);
            setSerpData(transformedData);
        } else {
            setSerpData([]);
        }
    }, [data, serpIsLoading, serpIsError, reset, acquire]);

    const hasData =
        data?.SerpFeaturesPresenceRecords?.some((item) => Object.keys(item?.SerpFeaturesPresence || {}).length > 0) ||
        false;

    return (
        <SERPFeaturesComposition
            data={serpData}
            isLoading={serpIsLoading}
            isError={serpIsError}
            hasData={hasData}
            tooltip={translate("website.analysis.keywords.overview.serp_features_composition.subtitle")}
            acquire={acquire}
            release={release}
        />
    );
};
